import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * LivePriceProvider - single source of truth for the ticking AONE/USDT price.
 *
 * - Fetches the current backend "anchor" price every 15s.
 * - Between anchors, runs a client-side random walk at 100ms (±2% band with
 *   mean-reversion toward the launch price) so every page renders a live,
 *   globally-consistent ticker.
 */

const LivePriceContext = createContext({
    price: 0.0001,
    prev: 0.0001,
    changePct: 0,
    volatilityPct: 2,
    tickMs: 100,
});

const TICK_MS = 100;
const VOL = 0.02;         // per-second std deviation
const MEAN_REVERT_STRENGTH = 0.02;
const BAND_MIN = 0.00006;
const BAND_MAX = 0.00120;

export function LivePriceProvider({ children }) {
    const [price, setPrice] = useState(0.0001);
    const [prev, setPrev] = useState(0.0001);
    const priceRef = useRef(0.0001);
    const anchorRef = useRef(0.0001);

    // Sync anchor with backend
    useEffect(() => {
        let cancelled = false;
        const sync = async () => {
            try {
                const { data } = await api.get("/network/price");
                if (cancelled) return;
                anchorRef.current = data.price_usdt;
                priceRef.current = data.price_usdt;
                setPrice(data.price_usdt);
            } catch { /* ignore */ }
        };
        sync();
        const id = setInterval(sync, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // 100ms random walk
    useEffect(() => {
        const id = setInterval(() => {
            const px = priceRef.current;
            const anchor = anchorRef.current;
            // Per-tick std dev scaled down (100ms of a ~30s window)
            const perTickVol = VOL / Math.sqrt(300);
            const gauss = (Math.random() + Math.random() + Math.random() + Math.random() +
                           Math.random() + Math.random() - 3) / 3; // ~N(0,1)
            const shock = gauss * perTickVol * px;
            const drift = MEAN_REVERT_STRENGTH * (anchor - px) * (TICK_MS / 1000);
            let next = px + drift + shock;
            if (next < BAND_MIN) next = BAND_MIN;
            if (next > BAND_MAX) next = BAND_MAX;
            setPrev(px);
            priceRef.current = next;
            setPrice(next);
        }, TICK_MS);
        return () => clearInterval(id);
    }, []);

    const launch = 0.0001;
    const changePct = ((price - launch) / launch) * 100;

    return (
        <LivePriceContext.Provider value={{ price, prev, changePct, volatilityPct: VOL * 100, tickMs: TICK_MS }}>
            {children}
        </LivePriceContext.Provider>
    );
}

export const useLivePrice = () => useContext(LivePriceContext);
